import React, { useCallback, useEffect, useMemo, useState } from 'react';
import data from './data';
import { formatPayToParties } from './logic';
import { DataList } from 'components/select-advanced';
import { FIELD_NAMES } from './constants';
import { costAndRevErrorMessage } from 'views/job-costing/utils/models';
import { IVendorsData } from './interfaces';
import { Input } from 'components/input/IndexAdvanced';

interface IProps {
	vendorsApiQueryParams: { country: string, company: string, purchase: string; };
	isAdding: boolean;
	payToParty: string;
	onUpdate: (key: string, value: string | number, label?: string) => void,
	errorList: string[];
	isDisabled: boolean;
	showErrorModal: (message: string) => void;
	isAthenaRateField?: boolean;
	payToPartyLabel?: string;
}

const PayToPartyComponent = (props: IProps) => {
	const {
		vendorsApiQueryParams,
		isAdding,
		payToParty,
		onUpdate,
		errorList,
		isDisabled,
		showErrorModal,
		isAthenaRateField,
		payToPartyLabel = 'Pay To Party'
	} = props;

	const {
		country,
		company,
		purchase
	} = vendorsApiQueryParams;

	const [payToPartyRawData, setPayToPartyRawData] = useState<IVendorsData[]>([]);
	const [hasError, setHasError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const payToPartyOptions = useMemo(() => formatPayToParties(payToPartyRawData), [payToPartyRawData]);
	const hasErrorForField = (fieldName: string) => Array.isArray(errorList) && errorList.includes(fieldName);

	const getAllPayToPartiesData = useCallback(
		async (countryCode: string, companyCode: string, purchaseOrganisation: string) => {
			try {
				const response = await data.api.getAllPayToParties(countryCode, companyCode, purchaseOrganisation);
				setPayToPartyRawData(response);
			} catch (e) {
				console.error(e);
				showErrorModal('Failed to fetch pay to party data');
			}
		},
		[]
	);

	useEffect(() => {
		const countryCode = country || 'ZZ';
		getAllPayToPartiesData(countryCode, company, purchase);
	}, [country, company, purchase, getAllPayToPartiesData]);

	useEffect(() => {
		if (!payToParty && hasErrorForField(FIELD_NAMES.PARTY_CODE)) {
			setHasError(true);
			setErrorMessage(costAndRevErrorMessage.SIMPLE_ERROR);
		} else if (!isAdding && hasErrorForField(FIELD_NAMES.ICB)) {
			setHasError(true);
			setErrorMessage(costAndRevErrorMessage.validationMsgCostandRev);
		} else {
			setHasError(false);
			setErrorMessage('');
		}
	}, [payToParty, errorList, isAdding]);

	const onPayToPartyDropDownChange = (value: number | string) => {
		setHasError(false);
		setErrorMessage('');

		const selectedPayToParty = payToPartyRawData.find((vendor) => vendor.vendorCode === value);
		if (!selectedPayToParty) {
			return;
		}

		const { vendorCode, country: partyCountry, scacCode, vendorName, interCompany } = selectedPayToParty;
		const partyDetails = `${partyCountry}|${vendorCode}|${scacCode}|${vendorName}`;
		const icbIndicator = interCompany && interCompany.toLowerCase() !== 'n' ? 'IC' : '-';

		onUpdate(FIELD_NAMES.PARTY_CODE, value);
		onUpdate(FIELD_NAMES.PARTY_NAME, vendorName);
		if (!isAthenaRateField) {
			onUpdate(FIELD_NAMES.PARTY_DETAILS, partyDetails);
			onUpdate(FIELD_NAMES.ICB, icbIndicator);
		}
	};

	const onPayToPartyInputChange = (value: string) => {
		setHasError(false);
		setErrorMessage('');
		onUpdate(FIELD_NAMES.PARTY_CODE, value);
	};

	return (
		<React.Fragment>
			{payToPartyRawData.length > 0 &&
				<DataList
					id="payToPartyDropDown"
					className="payToPartyDropDown"
					label={payToPartyLabel}
					value={payToParty}
					data={payToPartyOptions}
					isDisabled={isDisabled}
					onChange={onPayToPartyDropDownChange}
					hasError={hasError}
					error={errorMessage}
				/>
			}

			{payToPartyRawData.length === 0 &&
				<Input
					type="text"
					id="payToPartyInput"
					className="payToPartyInput"
					label="Pay To Party"
					value={payToParty}
					onChange={onPayToPartyInputChange}
					hasError={hasError}
					error={errorMessage}
				/>
			}
		</React.Fragment>
	);
};

export default PayToPartyComponent;